// Store modules → phone layout input, and the workspace's measured metrics.

import type { BentoModule } from "../store/module.js";
import { clampH, metricsFor, type LayoutItem, type Metrics } from "./layout.js";

/** Can this module sit at half width on a phone? ("mobileSmall" in
 *  modules.json; falls back to the desktop width before the config loads.) */
export function isSmallModule(m: BentoModule): boolean {
  const small = window.ModuleLoader?.isMobileSmall?.(m.title);
  if (small !== undefined) return small;
  const cols =
    window.ModuleLoader?.getDefaultSize?.(m.title, { cols: m.cols, rows: m.rows }).cols ?? m.cols;
  return cols <= 2;
}

/** Phone height in half-widths: the module's own setting, else "mobileH" in
 *  modules.json, else from its desktop height — short (≤2 rows) = 1.25,
 *  medium (3) = 2.25, tall = 3.75. */
export function mobileHeight(m: BentoModule): number {
  if (m.mobileUnits) return clampH(m.mobileUnits);
  const preset = window.ModuleLoader?.getMobileHeight?.(m.title);
  if (preset !== undefined) return clampH(preset);
  return m.rows <= 2 ? 1.25 : m.rows === 3 ? 2.25 : 3.75;
}

export function layoutItems(modules: BentoModule[]): LayoutItem[] {
  return modules.map((m) => ({
    id: m.id,
    small: isSmallModule(m),
    h: mobileHeight(m),
    pairNext: m.mobilePairNext,
  }));
}

export function readMetrics(ws: HTMLElement): Metrics {
  const cs = getComputedStyle(ws);
  return metricsFor(ws.clientWidth, {
    left: parseFloat(cs.paddingLeft) || 0,
    right: parseFloat(cs.paddingRight) || 0,
    top: parseFloat(cs.paddingTop) || 0,
    bottom: parseFloat(cs.paddingBottom) || 0,
  });
}

/** Phone layout breakpoint (matches main.js's isMobile). */
export const MOBILE_QUERY = "(max-width: 768px)";
